import { autoinject, bindable, DOM } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import { GuidHelper } from "../../helpers/guid.helper";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../helpers/reflection";
import { StringHelper } from "../../helpers/string.helper";
import { BindingSignaler } from "aurelia-templating-resources";
//import 'scripts/proposal-intl-formatToParts-master/polyfill.js';

//Load styles
require("./comp-text-box.scss");

@autoinject
@transient()
export class CompTextBox {

    public static FilterModes = {
        blur: "blur",
        focus: "focus",
        change: "change"
    };

    private _vm = this;

    public isPasswordVisible: boolean = false;
    public isAttached: boolean;
    public previousValue: string;
    public currentValue: string;
    public decimalSeparator: string = ".";
    public thousandSeparator: string = ",";
    public valueSignaler: string;
    public input: HTMLInputElement;
    public diffLength: number = 0;

    @bindable
    public label: string = "";

    @bindable
    public stopPropagationOnClick: boolean = true;

    @bindable
    public startLabel: string = "";

    @bindable
    public filter: (value: string) => string;

    @bindable
    public endLabel: string = "";

    @bindable
    public readonly: boolean = false;

    @bindable
    public disabled: boolean = false;

    @bindable
    public type: string = "text";

    @bindable
    public nullWhenEmpty: boolean = false;

    @bindable
    public value: string = "";

    @bindable
    public maxLength: number = 99999;

    @bindable
    public filterMode: string = CompTextBox.FilterModes.change;

    @bindable
    public placeholder: string = "";

    @bindable
    public culture: string = "";

    @bindable
    public pattern: string = "";

    @bindable
    public decimalPlaces: number = 2;

    @bindable
    public info: string = "";

    @bindable
    public min: number = 0;

    @bindable
    public max: number = 2147483647;

    @bindable
    public identifier: string;

    @bindable
    public allowNegative: boolean = false;

    @bindable
    public tab: number;

    @bindable
    public isFocused: boolean;

    @bindable
    public enableTogglePassword: boolean = false;

    @bindable
    public autoComplete: string = "new-password";

    @bindable
    public useNumberGrouping: boolean = true;

    @bindable
    public startIcon: string;

    @bindable
    public canClearText: boolean;

    @bindable
    public showStartLabelInReadonly: boolean = false;

    @bindable
    public size: string = "medium";

    @bindable
    public infoBottom: string;

    @computedFrom(Reflection.getPropertyPath<CompTextBox>(x => x._vm))
    public get vm(): CompTextBox {
        return this._vm;
    }

    @computedFrom(
        Reflection.getPropertyPath<CompTextBox>(x => x.type),
        Reflection.getPropertyPath<CompTextBox>(x => x.isPasswordVisible)
    )
    public get internalType(): string {
        switch (this.type) {
            // to show numeric keyboard on mobile
            case "numeric-password":
                if (this.isPasswordVisible) {
                    return "text";
                }
                break;
            case "double":
            case "float":
            case "integer":
            case "digits":
                return "tel";
            case "password":
                if (this.isPasswordVisible) {
                    return "text";
                }
                break;
            default:
                return this.type;
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<CompTextBox>(z => z.startLabel),
        Reflection.getPropertyPath<CompTextBox>(z => z.readonly),
        Reflection.getPropertyPath<CompTextBox>(z => z.value)
    )
    public get canShowStartLabel(): boolean {
        return this.startLabel && (!this.readonly || (this.readonly && this.value != null));
    }

    @computedFrom(
        Reflection.getPropertyPath<CompTextBox>(z => z.endLabel),
        Reflection.getPropertyPath<CompTextBox>(z => z.readonly),
        Reflection.getPropertyPath<CompTextBox>(z => z.value)
    )
    public get canShowEndLabel(): boolean {
        return this.endLabel && (!this.readonly || (this.readonly && this.value != null));
    }

    @computedFrom(Reflection.getPropertyPath<CompTextBox>(x => x.type))
    public get isNumeric(): boolean {
        return this.type == "double" || this.type == "float" || this.type == "integer" || this.type == "digits";
    }

    public constructor(private element: Element, private bindingSignaler: BindingSignaler) {
        this.identifier = GuidHelper.generateGuid();
        this.valueSignaler = "textbox_" + this.identifier;
        this.setDecimalSeparator();
    }

    public attached(): void {
        this.isAttached = true;
        if (this.input) {
            this.input.addEventListener("click", this.onClick, false);
            this.input.addEventListener("keydown", this.onKeyDown, false);
            this.input.addEventListener("cut", this.cancelEvent, false);
            this.input.addEventListener("copy", this.cancelEvent, false);
            this.bindingSignaler.signal(this.valueSignaler);
        }
    }

    public valueChanged(newValue: string, oldValue: string): void {
        this.registerChanges(oldValue, newValue);
        setTimeout(() => {
            this.raiseTextChangedEvent(oldValue, newValue);
        });
    }

    public propertyChanged(property: string): void {
        switch (property) {
            case "decimalPlaces":
            case "type":
            case "readonly":
            case "disabled":
                {
                    if (this.input) {
                        this.bindingSignaler.signal(this.valueSignaler);
                    }
                }
                break;
            case "culture":
                {
                    this.setDecimalSeparator();

                    if (this.input) {
                        this.bindingSignaler.signal(this.valueSignaler);
                    }
                }
                break;
            default:
                break;
        }
    }

    private setDecimalSeparator() {
        const num: number = 1000000.1000000;

        if (!this.culture || this.culture.length == 0) {
            this.culture = window.navigator.language;
        }

        const parts: any[] = Intl.NumberFormat(this.culture)["formatToParts"](num);

        this.decimalSeparator = parts.filter(x => x.type == "decimal")[0].value;
        this.thousandSeparator = parts.filter(x => x.type == "group")[0].value;
    }

    public onFocus($event: Event): void {
        this.isFocused = true;
        const event: CustomEvent = DOM.createCustomEvent("focus", $event);
        this.element.dispatchEvent(event);
        if (this.filterMode.toLowerCase() === CompTextBox.FilterModes.focus) {
            if (this.filter) {
                this.value = this.filter((<any>$event.target).value);
            }
        }
    }

    public focusTextBox(selectAlltext: boolean = false): void {
        const input: HTMLInputElement = this.input;
        setTimeout(() => {
            input.focus();
            if (selectAlltext) {
                input.select();
            }
        });
    }

    public onBlured($event: Event): void {
        this.isFocused = false;
        this.bindingSignaler.signal(this.valueSignaler);
        if (this.filterMode.toLowerCase() === CompTextBox.FilterModes.blur) {
            if (this.filter) {
                this.value = this.filter((<any>$event.target).value);
            }
        }
        const event: CustomEvent = DOM.createCustomEvent("blur", $event);
        this.element.dispatchEvent(event);
    }

    public clearField(): void {
        this.value = null;
    }

    public togglePasswordVisibility(): void {
        this.isPasswordVisible = !this.isPasswordVisible;
    }

    public detached(): void {
        if (this.input) {
            this.input.removeEventListener("click", this.onClick, false);
            this.input.removeEventListener("keydown", this.onKeyDown, false);
            this.input.removeEventListener("cut", this.cancelEvent, false);
            this.input.removeEventListener("copy", this.cancelEvent, false);
        }
    }

    private onClick = ($event: Event): void => {
        if ($event && this.stopPropagationOnClick) {
            $event.stopPropagation();
        }
    }

    private onKeyDown = (event: Event): void => {
        const changeTextEvent: CustomEvent = new CustomEvent("key-down", { detail: event, bubbles: true });
        this.element.dispatchEvent(changeTextEvent);
    }

    private cancelEvent = (event: Event): boolean => {
        if (this.type === "numeric-password") {
            this.stopEvent(event);
            return false;
        }
        return true;
    }

    private stopEvent(e): void {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
        e.cancelBubble = true;
        e.returnValue = false;
    }

    private registerChanges(oldValue: string, newValue: string): void {
        this.previousValue = oldValue;
        this.currentValue = newValue;
    }

    private raiseTextChangedEvent(oldValue: string, newValue: string): void {
        const paramsEvent: any = { detail: { oldValue: oldValue, newValue: newValue }, bubbles: true };
        const changeTextEvent: CustomEvent = new CustomEvent("textchange", paramsEvent);
        this.element.dispatchEvent(changeTextEvent);
    }

    //Clear text from the textbox
    clearText() {
        this.value = "";
    }

    //Increase value for numeric values
    public increaseValue() {
        if (this.isNumeric) {
            if (!this.value) {
                this.value = "1";
            } 
            else {
                this.value = (parseFloat(this.value) + 1).toString();
            }
        }
    }

    //Decrease value for numeric values
    public decreaseValue() {
        if (this.isNumeric) {
            if (!this.value && this.min < 0) {
                this.value = "-1";
            } 
            else if(this.min < Number(this.value)) {
                this.value = (parseFloat(this.value) - 1).toString();
            }
        }
    }
}

@autoinject
@transient()
export class FormatterValueConverter {

    public isNegative(n) {
        return ((n = +n) || 1 / n) < 0;
    }

    public getSign(value) {
        if (this.isNegative(value)) {
            return "-";
        } else {
            const firstChar: string = value && value.length > 0 ? value[0] : "";
            return firstChar == "+" || firstChar == "-" ? firstChar : "";
        }
    }

    public repeat(text: string, count: number): string {
        "use strict";
        if (text == null) {
            throw new TypeError("can't convert " + text + " to object");
        }
        let str = "" + text;
        count = +count;
        if (count !== count) {
            count = 0;
        }
        if (count < 0) {
            throw new RangeError("repeat count must be non-negative");
        }
        if (count === Infinity) {
            throw new RangeError("repeat count must be less than infinity");
        }
        count = Math.floor(count);
        if (str.length === 0 || count === 0) {
            return "";
        }
        // ensuring count is a 31-bit integer allows us to heavily optimize the
        // main part. But anyway, most current (August 2014) browsers can't handle
        // strings 1 << 28 chars or longer, so:
        if (str.length * count >= 1 << 28) {
            throw new RangeError("repeat count must not overflow maximum string size");
        }
        let rpt = "";
        for (; ;) {
            if ((count & 1) == 1) {
                rpt += str;
            }
            count >>>= 1;
            if (count == 0) {
                break;
            }
            str += str;
        }
        // could we try:
        // return Array(count + 1).join(text);
        return rpt;
    }

    public toView(value: any | string, param: any): string {

        const textBox: CompTextBox = param.vm as CompTextBox;

        if (value == null || value == undefined) {
            value = "";
        }

        const sign = this.getSign(value);

        if (typeof value == 'number') {
            value = value.toString();
        }

        if ((sign == "+" || sign == "-") && value[0] == sign && ["double", "float", "integer", "digits"].indexOf(textBox.type) > -1) {
            value = value.substr(1);
        }

        if (textBox) {
            switch (textBox.type) {
                case "double":
                case "float":
                    {
                        if (value && value.toString() == "NaN") {
                            value = "";
                        }
                        if (value && textBox.input && textBox.input.value) {
                            // values will look like 13.4 (the dot separator is from ECMA Specification so OS indipendent)
                            // https://tc39.es/ecma262/#sec-static-semantics-mv
                            const valueFromInput = textBox.input.value
                                .replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "")
                                .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                            if (Number(value) == Number(valueFromInput)) {
                                value = valueFromInput;
                            }
                        }
                        const valueBeforeFormatting = value;
                        let decimal = (value ? (value.split('.')[1] || "") : "").substr(0, textBox.decimalPlaces);
                        const options: Intl.NumberFormatOptions = {
                            maximumFractionDigits: textBox.decimalPlaces,
                            minimumFractionDigits: textBox.readonly || textBox.disabled ? textBox.decimalPlaces : 0,
                            useGrouping: textBox.useNumberGrouping
                        };
                        if (value) {
                            value = Number(value).toFixed(textBox.decimalPlaces);
                            value = Intl.NumberFormat(textBox.culture || window.navigator.language, options).format(value);
                        }

                        const valueAfterFormatting = value
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.thousandSeparator), 'g'), "")
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                        if (valueBeforeFormatting && valueBeforeFormatting != valueAfterFormatting) {
                            const index = value.indexOf(textBox.decimalSeparator);
                            value = value + (index === -1 ? textBox.decimalSeparator : "");
                            if (!textBox.isFocused) {
                                let fractionalPart = "";
                                let dec = value.split(textBox.decimalSeparator)[1];
                                if (dec) {
                                    if (dec.length < textBox.decimalPlaces) {
                                        fractionalPart = this.repeat("0", textBox.decimalPlaces - dec.length);
                                    }
                                    dec = dec + fractionalPart;
                                } else {
                                    dec = this.repeat("0", textBox.decimalPlaces);
                                }
                                decimal = dec;
                                value = value + decimal;
                            }
                        }
                        if (value && decimal) {
                            value = value.split(textBox.decimalSeparator)[0] + textBox.decimalSeparator + decimal;
                        }
                        if (value === "NaN" || (value && value.toString().indexOf("NaN") > -1)) {
                            value = "";
                        }
                        if ((sign === "-" || sign === "+") &&
                            value.indexOf(sign) === -1 &&
                            textBox.allowNegative) {
                            value = sign + value;
                        }

                        //If number ends with . like 38. and decimalPlaces is 0 then we need to remove the dot
                        if (value && StringHelper.endsWith(value, textBox.decimalSeparator) && textBox.decimalPlaces == 0) {
                            value = value.substr(0, value.length - 1);
                        }
                    }
                    break;
                case "integer":
                case "digits":
                case "numeric-password":
                    {
                        if (value && value.toString() == "NaN") {
                            value = "";
                        }
                        const options: Intl.NumberFormatOptions = {
                            maximumFractionDigits: 0,
                            useGrouping: textBox.useNumberGrouping
                        };
                        value = this.removeAllButLast(value, textBox.decimalSeparator);
                        value = value ? value.replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "") : value;
                        if (value) {
                            switch (textBox.type) {
                                case "integer":
                                    value = Number(value).toFixed(0);
                                    break;
                            }
                            value = Intl.NumberFormat(textBox.culture || window.navigator.language, options).format(value);
                        }
                        if (value === "NaN") {
                            value = "";
                        }
                        if ((sign === "-" || sign === "+") &&
                            value.indexOf(sign) === -1 &&
                            textBox.allowNegative) {
                            value = sign + value;
                        }
                    }
                    break;
                default:
                    break;
            }
        }
        return value;
    }
    public fromView(value: any, param: any): string {
        if (value && typeof value !== "string") {
            value = value.toString();
        }
        const textBox: CompTextBox = param.vm as CompTextBox;
        const fromViewValue: string = value;
        let caretIndex: number = null;
        let isNumber: boolean = false;
        const firstChar: string = value && value.length > 0 ? value[0] : "";
        if (textBox) {
            switch (textBox.type) {
                case "double":
                case "float":
                    {
                        caretIndex = textBox.input.selectionStart;
                        value = this.removeAllButLast(value, textBox.decimalSeparator);
                        value = value
                            .replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "")
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                        const decimal = value.split(".")[1];

                        if (decimal && decimal.length > textBox.decimalPlaces) {
                            value = Number(value).toFixed(textBox.decimalPlaces);
                        }

                        if ((firstChar === "-" || firstChar === "+") &&
                            value.indexOf(firstChar) === -1 &&
                            textBox.allowNegative) {
                            value = firstChar + value;
                        }
                        isNumber = true;
                        break;
                    }
                case "integer":
                case "digits":
                    isNumber = true;
                    break;
                case "numeric-password":
                    {
                        caretIndex = textBox.input.selectionStart;
                        value = value.replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "");
                        if ((firstChar === "-" || firstChar === "+") &&
                            value.indexOf(firstChar) === -1 &&
                            textBox.allowNegative) {
                            value = firstChar + value;
                        }
                        isNumber = true;
                        break;
                    }
                case "text":
                    {
                        caretIndex = textBox.input.selectionStart;
                    }
                    break;
                case "email":
                    {
                        return value;
                    }
                default:
                    break;
            }
        }
        if (textBox.filterMode.toLowerCase() === CompTextBox.FilterModes.focus) {
            if (textBox.filter) {
                value = textBox.filter(value);
            }
        }

        if (isNumber) {
            textBox.input.value = this.toView(value, param);
            textBox.diffLength = Math.max(0, textBox.input.value.replace(new RegExp(`[^${textBox.thousandSeparator}]`, 'g'), "").length - textBox.diffLength);
            if (caretIndex != null) {
                caretIndex = caretIndex + Math.min(1, Math.max(0, (fromViewValue.lastIndexOf(textBox.decimalSeparator) == (fromViewValue.length - 1) ? 0 : textBox.diffLength)));
                textBox.input.setSelectionRange(caretIndex, caretIndex);
            }
        }

        if (textBox.nullWhenEmpty && value === "") {
            value = null;
        }
        return isNumber && value && !isNaN(value) ? Number(value) : value;
    }

    public removeAllButLast(str: string, token: string): string {
        const parts: string[] = str ? str.split(token) : [str];
        if (parts[1] === undefined) {
            return str;
        } else {
            return parts.slice(0, -1).join("") + token + parts.slice(-1);
        }
    }
}
